import { LadiesMain } from "@modules/global/ladies-main"
import type Swiper from "swiper"
import type { SwiperContainer } from "swiper/element"
import { Keyboard, Manipulation, Mousewheel, Navigation, Thumbs, Zoom } from "swiper/modules"
import type { SwiperOptions } from "swiper/types"
import { initializeGalleryAds, initializeThumbnailAds } from "./gallery-ads"
import { galleryModal, isGalleryOpen } from "./gallery-modal"
import { pauseVideos } from "./gallery-videos"

export const gallerySlider = (): SwiperContainer | null =>
    galleryModal()?.querySelector(".gallery-slider") as SwiperContainer | null
const thumbnailSlider = (): SwiperContainer | null =>
    galleryModal()?.querySelector(".thumbnail-slider") as SwiperContainer | null
const galleryText = (): HTMLElement | null =>
    gallerySlider()?.querySelector(".gallery-info .gallery-text") as HTMLElement | null
const counter = (): HTMLElement | null =>
    gallerySlider()?.querySelector(".gallery-info .image-count") as HTMLElement | null

const injectAds: (element: HTMLElement) => boolean = (element: HTMLElement): boolean =>
    element.getAttribute("data-gallery-type") !== "fkk" && element.getAttribute("data-gallery-type") !== "upgradedStar"

const GalleryUpdateEvent = "gallery:updated"
const ThumbnailUpdateEvent = "thumbnails:updated"
type SwiperEvent = CustomEvent<{ swiper: Swiper }>

declare global {
    interface HTMLElementEventMap {
        [GalleryUpdateEvent]: SwiperEvent
        [ThumbnailUpdateEvent]: SwiperEvent
    }
}

export function initializeSwiper(): void {
    initializeThumbnailSwiper()
    initializeGallerySwiper()

    const thumbs = thumbnailSlider()?.swiper as Swiper | undefined
    const main = gallerySlider()?.swiper as Swiper | undefined
    if (!thumbs || !main) return

    // Let layout settle (CSS/fonts, container sizing)
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            thumbs.update()
            thumbs.slideToLoop(main.realIndex, 0)
            thumbs.updateProgress()
        })
    })
}

export function resetSwiperAnimatingProperty(): void {
    const gallerySwiperElement: SwiperContainer | null = gallerySlider()
    if (!gallerySwiperElement) {
        return
    }

    const swiper: Swiper | null = gallerySwiperElement.swiper as Swiper | null
    if (!swiper) {
        return
    }

    swiper.animating = false
}

function calculateInitialSlide() {
    const hashIncludesGallery = /([#&])gallery=\d+/.test(location.hash)
    if (!hashIncludesGallery) {
        return 0
    }
    const galleryMatch = location.hash.match(/([#&])gallery=(\d+)/)
    if (!galleryMatch?.[2]) {
        return 0
    }
    return Number.parseInt(galleryMatch[2], 10)
}

function initializeGallerySwiper(): void {
    const gallerySwiperElement: SwiperContainer | null = gallerySlider()
    const thumbnailSwiperElement: SwiperContainer | null = thumbnailSlider()
    if (!gallerySwiperElement || !thumbnailSwiperElement) {
        return
    }

    const previousNavigation: HTMLElement | null = gallerySwiperElement.querySelector(
        ".slider-navigation.previous",
    ) as HTMLElement | null
    const nextNavigation: HTMLElement | null = gallerySwiperElement.querySelector(
        ".slider-navigation.next",
    ) as HTMLElement | null

    Object.assign(gallerySwiperElement, {
        initialSlide: calculateInitialSlide(),
        modules: [Manipulation, Navigation, Keyboard, Mousewheel, Thumbs, Zoom],
        navigation: {
            enabled: true,
            prevEl: previousNavigation,
            nextEl: nextNavigation,
        },
        loop: true,
        thumbs: {
            swiper: thumbnailSwiperElement?.swiper,
        },
        keyboard: true,
        mousewheel: {
            sensitivity: 10,
            releaseOnEdges: true,
        },
        zoom: {
            maxRatio: 5,
        },
        on: {
            slidesUpdated(this: Swiper): void {
                gallerySwiperElement.dispatchEvent(
                    new CustomEvent(GalleryUpdateEvent, { detail: { swiper: this }, bubbles: true }),
                )
            },
            realIndexChange(this: Swiper): void {
                updateSliderHash(this)
                updateSliderText(this)
                updateSliderCounter(this)

                const thumbs = thumbnailSlider()?.swiper as Swiper | undefined
                if (!thumbs) return

                // Force thumbs to follow the real index (loop-safe)
                requestAnimationFrame(() => {
                    thumbs.slideToLoop(this.realIndex, 250)
                })
            },
            slideChange(this: Swiper): void {
                stopVideoPlayback()
            },
            slideChangeTransitionStart(this: Swiper): void {
                addTrackingPixel()
            },
        },
    } as SwiperOptions)

    if (injectAds(gallerySwiperElement)) {
        gallerySwiperElement.addEventListener(
            GalleryUpdateEvent,
            (swiperEvent: SwiperEvent): void => {
                initializeGalleryAds(gallerySwiperElement, swiperEvent.detail.swiper)
            },
            { once: true },
        )
    }

    gallerySwiperElement.initialize()

    const swiper = gallerySwiperElement.swiper as Swiper

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            // make sure swiper has applied loopFix and computed indices
            swiper.update()

            updateSliderHash(swiper)
            updateSliderText(swiper)
            updateSliderCounter(swiper)
        })
    })

    gallerySwiperElement.dispatchEvent(
        new CustomEvent(GalleryUpdateEvent, { detail: { swiper: gallerySwiperElement.swiper }, bubbles: true }),
    )
}

export function initializeThumbnailSwiper(): void {
    const gallerySwiperElement: SwiperContainer | null = gallerySlider()
    const thumbnailSwiperElement: SwiperContainer | null = thumbnailSlider()
    if (!thumbnailSwiperElement) {
        return
    }

    const previousNavigation: HTMLElement | null = thumbnailSwiperElement.querySelector(
        ".slider-navigation.previous",
    ) as HTMLElement | null
    const nextNavigation: HTMLElement | null = thumbnailSwiperElement.querySelector(
        ".slider-navigation.next",
    ) as HTMLElement | null

    Object.assign(thumbnailSwiperElement, {
        modules: [Manipulation, Navigation],
        navigation: {
            enabled: true,
            prevEl: previousNavigation,
            nextEl: nextNavigation,
        },
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        slidesOffsetBefore: 45,
        slidesOffsetAfter: 45,
        on: {
            slidesUpdated(this: Swiper): void {
                thumbnailSwiperElement.dispatchEvent(
                    new CustomEvent(ThumbnailUpdateEvent, { detail: { swiper: this }, bubbles: true }),
                )
            },
        },
    } as SwiperOptions)

    if (gallerySwiperElement && injectAds(gallerySwiperElement)) {
        thumbnailSwiperElement.addEventListener(
            ThumbnailUpdateEvent,
            (swiperEvent: SwiperEvent): void => {
                initializeThumbnailAds(swiperEvent.detail.swiper)
            },
            { once: true },
        )
    }

    thumbnailSwiperElement.initialize()
}

export function jumpToSlide(index: number): void {
    const swiper: Swiper | null = gallerySlider()?.swiper as Swiper | null
    if (!swiper) {
        return
    }

    swiper.slideToLoop(index)
    updateSliderHashFromIndex(index)
}

function updateSliderHash(swiper: Swiper): void {
    if (Number.isNaN(swiper.realIndex) || !isGalleryOpen()) {
        return
    }

    LadiesMain.addHashtagToUrl("gallery", String(swiper.realIndex))
}

function updateSliderHashFromIndex(realIndex: number): void {
    const swiper: Swiper | null = gallerySlider()?.swiper as Swiper | null
    if (!swiper) {
        return
    }

    LadiesMain.addHashtagToUrl("gallery", String(realIndex))
}

function updateSliderText(swiper: Swiper): void {
    const currentSlide: HTMLElement = swiper.slides[swiper.activeIndex] as HTMLElement
    if (!currentSlide) {
        return
    }

    const slideChild: HTMLElement | null = currentSlide.querySelector("[data-gallery-text]")
    const slideText: string = slideChild?.dataset.galleryText || ""
    const galleryTextContainer: HTMLElement | null = galleryText()

    if (galleryTextContainer !== null) {
        galleryTextContainer.textContent = slideText
    }

    // Show "Anzeige" instead of the counter if the slide is a third party advertisement
    if (slideText === "Anzeige") {
        counter()?.classList.add("hidden")
    } else {
        counter()?.classList.remove("hidden")
    }
}

function updateSliderCounter(swiper: Swiper): void {
    const slideCounter: HTMLElement | null = counter()
    if (!slideCounter) {
        return
    }

    const swiperContainer = swiper.el as HTMLElement
    const slides = swiper.slides as HTMLElement[]

    const isAdSlide = (slide: HTMLElement): boolean =>
        (slide.firstChild as HTMLElement | null)?.dataset?.galleryText === "Anzeige"
    const isDuplicateSlide = (slide: HTMLElement): boolean => slide.classList.contains("swiper-slide-duplicate")

    const originalSlides = slides.filter((slide) => !isDuplicateSlide(slide))

    const storedCount = swiperContainer.dataset.originalSlideCount
    const totalSlides = storedCount
        ? Number.parseInt(storedCount, 10)
        : originalSlides.filter((slide) => !isAdSlide(slide)).length

    const slideIsAdByIndex: Map<number, boolean> = new Map()
    for (const slide of originalSlides) {
        const indexAttr = slide.getAttribute("data-swiper-slide-index")
        if (indexAttr !== null) {
            const idx = Number.parseInt(indexAttr, 10)
            slideIsAdByIndex.set(idx, isAdSlide(slide))
        }
    }

    let currentSlide = 0
    for (let i = 0; i <= swiper.realIndex; i++) {
        if (!slideIsAdByIndex.has(i) || !slideIsAdByIndex.get(i)) {
            currentSlide++
        }
    }

    const replacements: Record<string, any> = {
        "{currentSlide}": currentSlide,
        "{totalSlides}": totalSlides,
    }

    const replaceExpression: RegExp = new RegExp(Object.keys(replacements).join("|"), "g")
    slideCounter.textContent =
        slideCounter.dataset.template?.replace(replaceExpression, (match: string): string => replacements[match]) || ""
}

function addTrackingPixel(): void {
    window.Tracking.trackGallery()
}

function stopVideoPlayback(): void {
    pauseVideos()
}
